import React, { useCallback } from "react";
import useAppNavigate from "../hooks/useAppNavigate";
import { motion } from "framer-motion";
import { parsePrice, formatPrice } from "../utils/priceUtils";
import type { ComboItem } from "../types/combo";

export type ComboCardVariant = "basic" | "classic" | "essential" | "prime";

export interface ComboCardProps {
  combo: ComboItem;
  variant?: ComboCardVariant;
  onAddToCart?: (combo: ComboItem) => void;
  onComboClick?: (combo: ComboItem) => void;
  className?: string;
  currency?: string;
  locale?: string;
  buttonColor?: string;
  fontFamily?: string;
  tpnActive?: boolean;
}

/* -------------------- Utils -------------------- */
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Utility to create light version of primary color for card backgrounds
const lightenColor = (hex: string, amount: number = 0.9) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#f9fafb'; // fallback to gray-50
  
  return `rgb(${Math.min(255, rgb.r + (255 - rgb.r) * amount)}, ${Math.min(255, rgb.g + (255 - rgb.g) * amount)}, ${Math.min(255, rgb.b + (255 - rgb.b) * amount)})`;
};

/* -------------------- Variant-Specific Components -------------------- */

// Basic Combo Card - Clean Minimalist Elegance
const BasicComboCard: React.FC<ComboCardProps> = ({ combo, onAddToCart, onComboClick, className, currency, locale, buttonColor = '#EF4444', fontFamily = '', tpnActive = false }) => {
  const navigate = useAppNavigate();
  const comboPrice = parsePrice(combo.combo_price);
  const offerPercentage = combo.offer_percentage || 0;

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (combo && onAddToCart) onAddToCart(combo);
  }, [onAddToCart, combo]);

  const handleComboClick = useCallback(() => {
    if (combo && combo.combo_id) {
      navigate(`/combo/${combo.combo_id}`);
      if (onComboClick) onComboClick(combo);
    }
  }, [navigate, onComboClick, combo]);

  return (
    <div
      className={`rounded-2xl shadow-lg border overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group bg-white ${fontFamily} ${className}`}
      style={{ 
        borderColor: lightenColor(buttonColor, 0.85),
        minHeight: '380px'
      }}
      onClick={handleComboClick}
      role="button"
      tabIndex={0}
    >
      {/* Improved Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {combo.combo_image ? (
          <img
            src={combo.combo_image}
            alt={combo.title || 'Combo'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${lightenColor(buttonColor, 0.9)}, ${lightenColor(buttonColor, 0.95)})` 
            }}
          >
            <span 
              className="text-2xl font-semibold"
              style={{ color: buttonColor }}
            >
              {combo.title?.charAt(0) || 'C'}
            </span>
          </div>
        )}
        {offerPercentage > 0 && (
          <span 
            className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold text-white rounded-md shadow-sm"
            style={{ backgroundColor: buttonColor }}
          >
            {offerPercentage}% OFF
          </span>
        )}
      </div>

      {/* Improved Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-sm leading-tight">
          {combo.title || 'Untitled Combo'}
        </h3>
        {combo.description && (
          <p className="text-gray-600 text-xs mb-3 line-clamp-2 leading-relaxed">
            {combo.description}
          </p>
        )}
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold" style={{ color: buttonColor }}>
            {formatPrice(comboPrice, currency, locale)}
          </span>
          {/* Text button - Only show when TPN is active */}
          {tpnActive && (
            <button
              onClick={handleAddToCart}
              className="px-4 py-2 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              style={{ backgroundColor: buttonColor }}
              onMouseEnter={(e) => {
                const rgb = hexToRgb(buttonColor);
                if (rgb) {
                  e.currentTarget.style.backgroundColor = `rgb(${Math.max(0, rgb.r - 20)}, ${Math.max(0, rgb.g - 20)}, ${Math.max(0, rgb.b - 20)})`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = buttonColor;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Add to Cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Classic Combo Card - Traditional Elegant Design
const ClassicComboCard: React.FC<ComboCardProps> = ({ combo, onAddToCart, onComboClick, className, currency, locale, buttonColor = '#EF4444', fontFamily = '', tpnActive = false }) => {
  const navigate = useAppNavigate();
  const comboPrice = parsePrice(combo.combo_price);
  const offerPercentage = combo.offer_percentage || 0;

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (combo && onAddToCart) onAddToCart(combo);
  }, [onAddToCart, combo]);

  const handleComboClick = useCallback(() => {
    if (combo && combo.combo_id) {
      navigate(`/combo/${combo.combo_id}`);
      if (onComboClick) onComboClick(combo);
    }
  }, [navigate, onComboClick, combo]);

  return (
    <motion.div
      className={`rounded-2xl shadow-xl border overflow-hidden hover:shadow-2xl transition-all duration-400 cursor-pointer group bg-white ${fontFamily} ${className}`}
      style={{ 
        borderColor: lightenColor(buttonColor, 0.6),
        boxShadow: `0 10px 30px -8px ${lightenColor(buttonColor, 0.7)}70, 0 0 0 1px ${lightenColor(buttonColor, 0.8)}`,
        minHeight: '420px'
      }}
      onClick={handleComboClick}
      role="button"
      tabIndex={0}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* Traditional Image with Compact Proportions */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {combo.combo_image ? (
          <img
            src={combo.combo_image}
            alt={combo.title || 'Combo'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ 
              background: `linear-gradient(to bottom right, ${lightenColor(buttonColor, 0.85)}, ${lightenColor(buttonColor, 0.9)})` 
            }}
          >
            <span 
              className="text-3xl font-serif font-bold"
              style={{ color: buttonColor }}
            >
              {combo.title?.charAt(0) || 'C'}
            </span>
          </div>
        )}
        {offerPercentage > 0 && (
          <div 
            className="absolute top-3 left-3 px-3 py-1 text-xs font-semibold text-white rounded-md shadow-lg"
            style={{ 
              backgroundColor: buttonColor,
              boxShadow: `0 4px 8px ${buttonColor}40`
            }}
          >
            {offerPercentage}% OFF
          </div>
        )}
        {/* Elegant overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
      </div>

      {/* Refined Content Section */}
      <div className="p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 leading-snug">
          {combo.title || 'Untitled Combo'}
        </h3>
        {combo.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
            {combo.description}
          </p>
        )}
        
        {/* Price Section */}
        <div className="mb-2">
          <div className="text-center">
            <span className="text-xl font-bold" style={{ color: buttonColor }}>
              {formatPrice(comboPrice, currency, locale)}
            </span>
          </div>
        </div>
        
        {/* Full-Width Add to Cart Button - Only show when TPN is active */}
        {tpnActive && (
          <button
            onClick={handleAddToCart}
            className="w-full py-3 text-white text-sm font-medium rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: buttonColor,
              boxShadow: `0 2px 8px ${buttonColor}30`
            }}
            onMouseEnter={(e) => {
              const rgb = hexToRgb(buttonColor);
              if (rgb) {
                e.currentTarget.style.backgroundColor = `rgb(${Math.max(0, rgb.r - 15)}, ${Math.max(0, rgb.g - 15)}, ${Math.max(0, rgb.b - 15)})`;
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${buttonColor}40`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = buttonColor;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 2px 8px ${buttonColor}30`;
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V16.5M9 19.5C9.8 19.5 10.5 20.2 10.5 21S9.8 22.5 9 22.5 7.5 21.8 7.5 21 8.2 19.5 9 19.5ZM20 19.5C20.8 19.5 21.5 20.2 21.5 21S20.8 22.5 20 22.5 18.5 21.8 18.5 21 19.2 19.5 20 19.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Add to Cart
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Essential Combo Card - Modern Elegant Vertical Layout
const EssentialComboCard: React.FC<ComboCardProps> = ({ combo, onAddToCart, onComboClick, className, currency, locale, buttonColor = '#EF4444', fontFamily = '', tpnActive = false }) => {
  const navigate = useAppNavigate();
  const comboPrice = parsePrice(combo.combo_price);
  const offerPercentage = combo.offer_percentage || 0;

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (combo && onAddToCart) onAddToCart(combo);
  }, [onAddToCart, combo]);

  const handleComboClick = useCallback(() => {
    if (combo && combo.combo_id) {
      navigate(`/combo/${combo.combo_id}`);
      if (onComboClick) onComboClick(combo);
    }
  }, [navigate, onComboClick, combo]);

  return (
    <motion.div
      className={`rounded-3xl shadow-xl border-0 overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group bg-white ${fontFamily} ${className}`}
      style={{ 
        boxShadow: `0 12px 35px -8px ${lightenColor(buttonColor, 0.7)}50`,
        minHeight: '460px'
      }}
      onClick={handleComboClick}
      role="button"
      tabIndex={0}
      whileHover={{ scale: 1.04, y: -6 }}
      transition={{ duration: 0.3 }}
    >
      {/* Modern Square Image */}
      <div className="relative aspect-square overflow-hidden">
        {combo.combo_image ? (
          <img
            src={combo.combo_image}
            alt={combo.title || 'Combo'}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-400"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${lightenColor(buttonColor, 0.85)}, ${lightenColor(buttonColor, 0.9)})` 
            }}
          >
            <span 
              className="text-5xl font-bold"
              style={{ color: buttonColor }}
            >
              {combo.title?.charAt(0) || 'C'}
            </span>
          </div>
        )}
        {offerPercentage > 0 && (
          <div 
            className="absolute top-4 right-4 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md"
            style={{ backgroundColor: buttonColor }}
          >
            {offerPercentage}% OFF
          </div>
        )}
      </div>

      {/* Modern Content Section */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 leading-tight">
          {combo.title || 'Untitled Combo'}
        </h3>
        {combo.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
            {combo.description}
          </p>
        )}
        
        {/* Price */}
        <div className="mb-4">
          <span className="text-2xl font-bold" style={{ color: buttonColor }}>
            {formatPrice(comboPrice, currency, locale)}
          </span>
        </div>
        
        {/* Modern Full-Width Button - Only show when TPN is active */}
        {tpnActive && (
          <button
            onClick={handleAddToCart}
            className="w-full py-3 text-white font-semibold rounded-xl transition-all duration-300 text-sm uppercase tracking-wide shadow-md hover:shadow-lg"
            style={{ backgroundColor: buttonColor }}
            onMouseEnter={(e) => {
              const rgb = hexToRgb(buttonColor);
              if (rgb) {
                e.currentTarget.style.backgroundColor = `rgb(${Math.max(0, rgb.r - 20)}, ${Math.max(0, rgb.g - 20)}, ${Math.max(0, rgb.b - 20)})`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = buttonColor;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Add to Cart
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Prime Combo Card - Luxury Elegant Design
const PrimeComboCard: React.FC<ComboCardProps> = ({ combo, onAddToCart, onComboClick, className, currency, locale, buttonColor = '#EF4444', fontFamily = '', tpnActive = false }) => {
  const navigate = useAppNavigate();
  const comboPrice = parsePrice(combo.combo_price);
  const offerPercentage = combo.offer_percentage || 0;

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (combo && onAddToCart) onAddToCart(combo);
  }, [onAddToCart, combo]);

  const handleComboClick = useCallback(() => {
    if (combo && combo.combo_id) {
      navigate(`/combo/${combo.combo_id}`);
      if (onComboClick) onComboClick(combo);
    }
  }, [navigate, onComboClick, combo]);

  return (
    <motion.div
      className={`rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer group w-full max-w-sm mx-auto bg-white ${fontFamily} ${className}`}
      style={{ 
        boxShadow: `0 25px 50px -15px ${lightenColor(buttonColor, 0.6)}60, 0 0 0 1px ${lightenColor(buttonColor, 0.8)}`,
        minHeight: '580px'
      }}
      onClick={handleComboClick}
      role="button"
      tabIndex={0}
      whileHover={{ y: -10, scale: 1.03 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Premium Large Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {combo.combo_image ? (
          <img
            src={combo.combo_image}
            alt={combo.title || 'Combo'}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${lightenColor(buttonColor, 0.85)}, ${lightenColor(buttonColor, 0.92)})` 
            }}
          >
            <span 
              className="text-6xl font-bold font-serif"
              style={{ color: buttonColor }}
            >
              {combo.title?.charAt(0) || 'C'}
            </span>
          </div>
        )}
        {offerPercentage > 0 && (
          <div 
            className="absolute top-5 left-5 text-white px-4 py-2 rounded-full text-sm font-bold shadow-xl"
            style={{ 
              backgroundColor: buttonColor,
              boxShadow: `0 8px 16px ${buttonColor}50`
            }}
          >
            {offerPercentage}% OFF
          </div>
        )}
        {/* Premium overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      {/* Luxury Content Section */}
      <div className="p-7 flex flex-col flex-1 space-y-6">
        <div className="flex-1 space-y-5">
          <h3 className="text-xl font-bold text-gray-900 leading-tight line-clamp-2">
            {combo.title || 'Untitled Combo'}
          </h3>
          {combo.description && (
            <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
              {combo.description}
            </p>
          )}
        </div>
        
        {/* Premium Price Section */}
        <div className="space-y-5">
          <div className="text-center">
            <span className="text-3xl font-bold" style={{ color: buttonColor }}>
              {formatPrice(comboPrice, currency, locale)}
            </span>
          </div>
          
          {/* Luxury Button - Only show when TPN is active */}
          {tpnActive && (
            <button
              onClick={handleAddToCart}
              className="w-full py-4 text-white font-bold rounded-2xl transition-all duration-400 text-sm uppercase tracking-widest shadow-xl hover:shadow-2xl"
              style={{ 
                backgroundColor: buttonColor,
                boxShadow: `0 8px 20px ${buttonColor}40`
              }}
              onMouseEnter={(e) => {
                const rgb = hexToRgb(buttonColor);
                if (rgb) {
                  e.currentTarget.style.backgroundColor = `rgb(${Math.max(0, rgb.r - 15)}, ${Math.max(0, rgb.g - 15)}, ${Math.max(0, rgb.b - 15)})`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 12px 28px ${buttonColor}50`;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = buttonColor;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 8px 20px ${buttonColor}40`;
              }}
            >
              Add to Cart
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};


/* -------------------- Main Combo Card Component -------------------- */
const ComboCard: React.FC<ComboCardProps> = React.memo(
  ({
    combo,
    variant = "basic",
    onAddToCart,
    onComboClick,
    className = "",
    currency = "USD",
    locale = "en-US",
    buttonColor,
    fontFamily,
    tpnActive = false,
  }) => {
    // Early return if combo is null/undefined
    if (!combo) {
      return null;
    }

    // Render the appropriate variant
    switch (variant) {
      case "basic":
        return (
          <BasicComboCard
            combo={combo}
            variant={variant}
            onAddToCart={onAddToCart}
            onComboClick={onComboClick}
            className={className}
            currency={currency}
            locale={locale}
            buttonColor={buttonColor}
            fontFamily={fontFamily}
            tpnActive={tpnActive}
          />
        );
      case "classic":
        return (
          <ClassicComboCard
            combo={combo}
            variant={variant}
            onAddToCart={onAddToCart}
            onComboClick={onComboClick}
            className={className}
            currency={currency}
            locale={locale}
            buttonColor={buttonColor}
            fontFamily={fontFamily}
            tpnActive={tpnActive}
          />
        );
      case "essential":
        return (
          <EssentialComboCard
            combo={combo}
            variant={variant}
            onAddToCart={onAddToCart}
            onComboClick={onComboClick}
            className={className}
            currency={currency}
            locale={locale}
            buttonColor={buttonColor}
            fontFamily={fontFamily}
            tpnActive={tpnActive}
          />
        );
      case "prime":
        return (
          <PrimeComboCard
            combo={combo}
            variant={variant}
            onAddToCart={onAddToCart}
            onComboClick={onComboClick}
            className={className}
            currency={currency}
            locale={locale}
            buttonColor={buttonColor}
            fontFamily={fontFamily}
            tpnActive={tpnActive}
          />
        );
      default:
        return (
          <BasicComboCard
            combo={combo}
            variant={variant}
            onAddToCart={onAddToCart}
            onComboClick={onComboClick}
            className={className}
            currency={currency}
            locale={locale}
            buttonColor={buttonColor}
            fontFamily={fontFamily}
            tpnActive={tpnActive}
          />
        );
    }
  }
);
ComboCard.displayName = "ComboCard";

export default ComboCard;
